import {Injectable} from "@angular/core";
import {UtilsService} from "./utils.service";
import {IndexedDB} from "./indexed-db.service";
import {RESTService} from "./rest.service";
import {StorageService} from "./storage.service";
import {GlobalStorageService} from "./global-storage.service";
import {firstValueFrom, zip} from "rxjs";

@Injectable({
  providedIn: 'root'
})
export class APIService {

  constructor(
    private utils: UtilsService,
    private db: IndexedDB,
    private rest: RESTService,
    private storage: StorageService,
    public global: GlobalStorageService) {
  }

  readClockInOutToday() {
    return new Promise((resolve, reject) => {
      const now = this.utils.getPacificDate().toISOString().substring(0, 10);
      const today = new Date(now);
      const tomorrow = new Date(new Date(now).getTime() + 86400000);
      this.db.readItemsRange('clockInOut', 'startDate', today, tomorrow).then((data: any) => {
        let total = 0;
        let totalMeal = 0;
        let isSkipped = false;
        let activeItem = {};
        for (let item of data) {
          if (item.type === 'work') {
            if (item.endDate) {
              total += item.endDate.getTime() - item.startDate.getTime();
            } else {
              total += this.utils.getPacificDate().getTime() - item.startDate.getTime();
              activeItem = item;
            }
          } else {
            if (item.endDate) {
              totalMeal += item.endDate.getTime() - item.startDate.getTime();
            } else {
              totalMeal += this.utils.getPacificDate().getTime() - item.startDate.getTime();
              activeItem = item;
            }
          }

          if (item['skipDate']) {
            isSkipped = true;
          }
        }
        resolve({total, totalMeal, isSkipped, activeItem})
      });
    });
  }

  async readFollowUpNotes(visitItem: any) {
    return new Promise(async (resolve, reject) => {
      const notes = await this.readVisitNotes(visitItem, 'Internal Note');
      resolve(notes);
    });
  }

  async readAllVisitNotes(visitItem: any) {
    return new Promise(async (resolve, reject) => {
      const internalNotes: any = await this.readVisitNotes(visitItem, 'Internal Note');
      const reportNotes: any = await this.readVisitNotes(visitItem, 'Report Note');
      const notes = [];
      for (const [key, value] of Object.entries(internalNotes)) {
        notes.push(value);
      }
      for (const [key, value] of Object.entries(reportNotes)) {
        notes.push(value);
      }
      reject(notes);
    });
  }

  async readVisitNotes(visitItem: any, typeOfNote: string) {
    const data = JSON.parse(JSON.stringify(visitItem));
    return new Promise(async (resolve, reject) => {
      const trainings: any = [];

      for (let item of data.trainingCategories) {
        if (!item) {
          break;
        }
        const comments: any = [];
        for (let comment of item.comments) {
          if (comment.isTrue) {
            const notes: any = await this.db.readItemsByIds('notes', comment.notesIds);
            comment.notes = notes.filter((item: any) => {
              if (!item.typeOfNote) {
                item.typeOfNote = item.noteType;
              }
              return item.typeOfNote === typeOfNote && !item.isCompleted
            });
            if (comment.notes.length) {
              comments.push(comment);
            }
          }
        }
      }

      /* Services */
      const services: any = [];
      for (let item of data.serviceCategories) {
        if (!item) {
          break;
        }
        const comments: any = [];
        for (let comment of item.comments) {
          if (comment.isTrue) {
            const notes: any = await this.db.readItemsByIds('notes', comment.notesIds);
            comment.notes = notes.filter((item: any) => {
              if (!item.typeOfNote) {
                item.typeOfNote = item.noteType;
              }
              return item.typeOfNote === typeOfNote && !item.isCompleted
            });
            if (comment.notes.length) {
              comments.push(comment);
            }
          }
        }
        if (comments.length) {
          services.push({category: item.name, comments});
        }
      }

      /* Education Modules */
      const educationModules: any = [];
      for (let item of data.educationModulesCategories) {
        if (!item) {
          break;
        }
        const comments = [];
        for (let comment of item.comments) {
          if (comment.isTrue) {
            const notes: any = await this.db.readItemsByIds('notes', comment.notesIds);
            comment.notes = notes.filter((item: any) => {
              if (!item.typeOfNote) {
                item.typeOfNote = item.noteType;
              }
              return item.typeOfNote === typeOfNote && !item.isCompleted
            });
            if (comment.notes.length) {
              comments.push(comment);
            }
          }
        }
        if (comments.length) {
          educationModules.push({category: item.name, comments});
        }
      }

      let picturesAndNotes: any = await this.db.readItemsByIds('notes', data.picturesAndNotes);
      picturesAndNotes = picturesAndNotes.filter((item: any) => {
        if (!item.typeOfNote) {
          item.typeOfNote = item.noteType;
        }
        return item.typeOfNote === typeOfNote && item.noteSource === 'Pictures and Notes' && !item.isCompleted
      });
      let flow: any = data.questions.filter((item: any) => item.answer);
      for (let f of flow) {
        f.isVisible = false;
        if (f['noteID']) {
          if (!f.note?.id) {
            f.note = await this.db.readItem('notes', f['noteID']);
          }
          if (f.note?.id) {
            if (!f.note.typeOfNote) {
              f.note.typeOfNote = f.note.noteType;
            }
            if (f.note.typeOfNote === 'Internal Note') {
              if (f.note.isCompleted) {
                delete f.note;
              } else {
                f.isVisible = true;
              }
            }
          }
        }

        const additionalNotes: any = f.additionalNotesIds?.length ? await this.db.readItemsByIds('notes', f.additionalNotesIds) : [];
        f.additionalNotes = additionalNotes.filter((item: any) => {
          if (!item.typeOfNote) {
            item.typeOfNote = item.noteType;
          }
          return item.typeOfNote === 'Internal Note' && !item.isCompleted
        });
        if (f.additionalNotes?.length) {
          for (let n of f.additionalNotes) {
            //n.img = await this.utils.readFile(n.filename);
            f.isVisible = true;
          }
        } else {
          f.additionalNotesIds = [];
        }

        for (let i = 0; i < f.comments?.length; i++) {
          if (!f.comments[i].isCompleted) {
            f.comments.splice(i, 1);
            i--;
          }
        }
      }
      flow = this.utils.sort(flow, 'templateCategoryOrder', 1);
      let lastCategory = '';
      for (let f of flow) {
        f.isCategoryVisible = f['templateCategoryOrder'] !== lastCategory;
        lastCategory = f['templateCategoryOrder'];
      }

      flow = flow.filter((item: any) => {
        return item.isVisible;
      });

      let siteInventory: any = data.siteInventory.filter((item: any) => item.noteID);

      for (let item of siteInventory) {
        if (!item.note?.id && item['noteID']) {
          item.note = await this.db.readItem('notes', item['noteID']);
        }
      }
      siteInventory = siteInventory.filter((item: any) => !item.note?.isCompleted);

      resolve({
        flow,
        picturesAndNotes,
        trainings,
        services,
        educationModules,
        siteInventory
      });
    });
  }

  stopClock() {
    const clock: any = this.global.get('clock') || {};

    clock.status = 'stopped';
    clock.endDate = this.utils.getPacificDate();
    this.db.updateItem('clockInOut', clock.id, clock).then((data: any) => {
      this.global.set('clock', clock);
      this.storage.setItem('clock', clock);
      this.global.set('isLoading', false);
    });
  }

  closeDay() {
    return new Promise(async (resolve, reject) => {
      this.global.set('isLoading', true);

      const clock = this.global.get('clock');
      switch (clock.status) {
        case 'started': {
          this.stopClock();
          break;
        }
        case 'lunch': {
          this.stopClock();
          break;
        }
      }

      this.db.readItems('routes').then(async (data: any) => {
        let isBackSyncRequired = false;
        let visitsToPush = 0;
        for (let route of data) {
          let isRouteUpdateRequired = false;
          for (let visit of route.visits) {
            if (!visit.isClosed && visit.status === 'Completed' && visit.siteVisitId) {
              visit.isCompleted = true;
              visit.isClosed = true;
              isRouteUpdateRequired = true;
              this.global.set('closeDayMessage', `Reading the visit for the site #${visit.name}`);
              const visitItem: any = await this.db.readItem('visits', visit.siteVisitId);
              if (visitItem?.id) {
                visitsToPush++;
                visitItem.isCompleted = visit.isCompleted;
                visitItem.isClosed = visit.isClosed;
                visitItem.finishDate = visit.finishDate;
                visitItem.status = visit.status;

                //updating Truck inventory values
                this.global.set('closeDayMessage', `Updating Truck inventory values fore the visit, site #${visit.name}`);
                const truckInventories: any = await this.db.readItems('truckInventoryTemplates') || [];
                let siteInventories = visitItem.siteInventory.filter((item: any) => item?.toDeliver);

                for (let siteInventory of siteInventories) {
                  let truckInventoryItem = truckInventories.find((truck: any) => truck.relatedInventory == siteInventory.relatedInventory);
                  if (truckInventoryItem?.id) {
                    truckInventoryItem.inventoryOnTruck -= siteInventory.toDeliver;
                    await this.db.updateItem('truckInventoryTemplates', truckInventoryItem.id, truckInventoryItem);
                  }
                }

                // Update the site template with the follow up notes and the last visit
                this.global.set('closeDayMessage', `Updating the site template with the follow up notes and the last visit, site #${visit.name}`);

                const site: any = await this.db.readItem('sitesTemplates', visitItem.site._id || visitItem.site.id) || {};
                if (!site.followUpNotes) {
                  site.followUpNotes = {
                    flow: [],
                    picturesAndNotes: [],
                    trainings: [],
                    services: [],
                    educationModules: [],
                    siteInventory: []
                  };
                }

                site.followUpNotes.flow = site.followUpNotes.flow.map((item: any) => {
                  item.id = this.utils.getUUIDv4();
                  return item;
                });

                let picturesAndNotes = site.followUpNotes.picturesAndNotes.filter((item: any) => {
                  return item.isCompleted;
                });
                picturesAndNotes = picturesAndNotes.map((item: any) => {
                  item.isPreviousVisit = true;
                  return item.portal_id;
                });
                visitItem.picturesAndNotes = visitItem.picturesAndNotes.concat(picturesAndNotes || []);

                site.followUpNotes.trainings = site.followUpNotes.trainings.map((item: any) => {
                  item.isPreviousVisit = true;
                  if (item.comments?.length) {
                    item.id = this.utils.getUUIDv4();
                    item.comments.map((comment: any) => {
                      comment.id = comment.portal_id;
                      return comment;
                    });
                    return item;
                  }
                });
                visitItem.trainingCategories = visitItem.trainingCategories.concat(site.followUpNotes.trainings || []);

                site.followUpNotes.services = site.followUpNotes.services.map((item: any) => {
                  item.isPreviousVisit = true;
                  if (item.comments?.length) {
                    item.id = this.utils.getUUIDv4();
                    item.comments.map((comment: any) => {
                      comment.id = comment.portal_id;
                      return comment;
                    });
                    return item;
                  }
                });
                visitItem.serviceCategories = visitItem.serviceCategories.concat(site.followUpNotes.services || []);
                site.followUpNotes.educationModules = site.followUpNotes.educationModules.map((item: any) => {
                  item.isPreviousVisit = true;
                  if (item.comments?.length) {
                    item.id = this.utils.getUUIDv4();
                    item.comments.map((comment: any) => {
                      comment.id = comment.portal_id;
                      return comment;
                    });
                    return item;
                  }
                });
                visitItem.educationModulesCategories = visitItem.educationModulesCategories.concat(site.followUpNotes.educationModules || []);
                visitItem.siteInventory = visitItem.siteInventory.concat(site.followUpNotes.siteInventory || []);

                site.followUpNotes = await this.readFollowUpNotes(visitItem);

                if (visitItem.visitType === "routine") {
                  site.lastVisit = visitItem.id;
                }
                site.lastVisitDateTime = this.utils.getPacificDateISO(visitItem.finishDate);

                this.global.set('closeDayMessage', `Updating site template, site #${visit.name}`);

                await this.db.updateItem('sitesTemplates', visitItem.site._id || visitItem.site.id, site);

                this.global.set('closeDayMessage', `Updating the visit, site #${visit.name}`);

                await this.utils.timeout(2000);
                await this.db.updateItem('visits', visitItem.id, visitItem);
              }
            }
          }
          if (isRouteUpdateRequired) {
            isBackSyncRequired = true;
            this.global.set('closeDayMessage', `Updating the route #${route.name}, ${route.scheduledDate}`);
            await this.db.updateItem('routes', route.id, route);
          }
        }
        this.storage.setItem('isBackSyncRequired', isBackSyncRequired.toString());
        this.global.set('isBackSyncRequired', isBackSyncRequired);
        this.global.set('closeDayMessage', `The day was closed`);
        resolve({message: 'The day was closed', isBackSyncRequired, visitsToPush});
      });
    });
  }

  readSyncData(visitsPerRequest: number, isSendAllFiles?: boolean) {
    return new Promise(async (resolve, reject) => {
      try {
        let data: any = {};
        let lastUpdate: any = await this.db.readItem('lastUpdate', 1) || {
          date: new Date('2025-03-03'),
          id: 1,
          type: 'back-sync'
        };

        data.user = await this.readSyncUser(lastUpdate.date);
        data.routes = await this.readSyncRoutes(lastUpdate.date);
        data.sites = await this.readSyncSites(lastUpdate.date);
        const user: any = this.storage.getItem('user');

        const itemsFromDB: any = await this.db.readItemsFrom('visits', '_updatedDate', lastUpdate.date) || [];
        let allVisits = itemsFromDB.filter((item: any) => {
          return item.user.email === user.email;
        });

        allVisits = allVisits.map((v: any) => {
          if (v._id) {
            v.status = "Completed";
          }
          return v;
        });
        allVisits = allVisits.filter((v: any) => {
          return v.status === "Completed";
        });
        allVisits = allVisits.sort((a: any, b: any) => {
          if (a._updatedDate > b._updatedDate) {
            return 1;
          }
          if (a._updatedDate < b._updatedDate) {
            return -1;
          }
          return 0;
        });
        let visitsToSync = allVisits.filter((item: any) => {
          return !item._id;
        });
        visitsToSync = visitsToSync.slice(0, visitsPerRequest);
        data.visitsToSync = visitsToSync;

        data.visits = await this.readSyncVisits(visitsToSync) || [];
        data.notes = await this.readSyncNotes(visitsToSync) || [];

        data.notesWithFiles = await this.readSyncNotes(isSendAllFiles ? allVisits : visitsToSync) || [];
        data.trainings = await this.readSyncTrainings(visitsToSync) || [];
        data.trainingsComments = await this.readSyncTrainingsComments(visitsToSync) || [];
        data.questions = await this.readSyncQuestions(visitsToSync) || [];
        data.questionsComments = await this.readSyncQuestionsComments(visitsToSync) || [];
        data.questionsTemplates = await this.readSyncQuestionsTemplates(visitsToSync, lastUpdate.date) || [];
        data.services = await this.readSyncServices(visitsToSync) || [];
        data.servicesComments = await this.readSyncServicesComments(visitsToSync) || [];
        data.educationModules = await this.readSyncEducationModules(visitsToSync) || [];
        data.educationModulesComments = await this.readSyncEducationModulesComments(visitsToSync) || [];
        data.inventories = await this.readSyncInventories(visitsToSync) || [];
        data.truckInventories = await this.readSyncTruckInventories(lastUpdate.date) || [];
        data.clockInOut = await this.readSyncClockInOut(lastUpdate.date) || [];

        resolve(data);
      } catch (e) {
        alert("Can't read data: " + JSON.stringify(e));
        reject({message: "Can't read data: " + JSON.stringify(e)})
      }
    });
  }

  readSyncUser(date: any) {
    return new Promise((resolve, reject) => {
      const user = this.storage.getItem('user');
      resolve(user);
    });
  }

  readSyncRoutes(date: any) {
    return new Promise(async (resolve, reject) => {
      const itemsFromDB: any = await this.db.readItemsFrom('routes', '_updatedDate', date) || [];
      const routes = this.filterDataFromDB(itemsFromDB);

      try {
        const formattedRoutes = routes.map((item: any) => {
          return {
            routeTemplateId: item.routeTemplateId,
            scheduledDate: item.scheduledDate,
            id: item.id,
            dateCreated: item._createdDate,
            name: item.name
          };
        });
        resolve(formattedRoutes);
      } catch (e) {
        alert(`Can't read routes:` + JSON.stringify(e));
        reject({message: e})
      }
    });
  }

  readSyncSites(date: any) {
    return new Promise(async (resolve, reject) => {
      const itemsFromDB: any = await this.db.readItemsFrom('sitesTemplates', '_updatedDate', date) || [];
      const sites = this.filterDataFromDB(itemsFromDB);
      try {
        for (const site of sites) {
          if (site.inventory?.length) {
            for (let inventory of site.inventory) {
              inventory.itemsInCase = inventory.itemsInCase || 0;
              inventory.parLevel = inventory.parLevel || 0;
            }
          }
          if (site && site['healthReport'] && site['healthReport']['filename'] && site['healthReport']['reportIsChanged']) {
            const base64: any = await this.utils.readFile(site['healthReport']['filename']);
            site['healthReport']['image'] = base64 ? base64.substring(base64.indexOf(',') + 1) : '';
          }
          delete site['healthReport']['img'];
        }
        resolve(sites);
      } catch (e) {
        alert(`Can't read sites:` + JSON.stringify(e));
        reject({message: e})
      }
    });
  }

  readSyncVisits(visitsToSync: any) {
    return new Promise(async (resolve, reject) => {
      try {
        const visits = [];

        for (const visit of visitsToSync) {
          let signature: string = '';
          if (visit.signature) {
            const base64: any = await this.utils.readFile(visit.signature);
            signature = base64 ? base64.substring(base64.indexOf(',') + 1) : '';
          }

          visits.push({
              id: visit.id,
              startDate: this.utils.getPacificDateISO(visit.startDate),
              finishDate: this.utils.getPacificDateISO(visit.finishDate),
              status: visit.status,
              visitType: visit.visitType === 'routine' ? 'Routine' : 'Special',
              routeType: visit.routeType,
              site: {
                id: visit.site?._id
              },
              route: {
                id: visit.route?.id
              },
              signature,
              maintenance: visit.maintenance,
              outOfStock: visit.outOfStock,
              training: visit.training
            }
          );
        }

        resolve(visits);
      } catch (e) {
        alert(`Can't read visits:` + JSON.stringify(e));
        reject({message: e})
      }
    });
  }

  readSyncQuestions(visitsToSync: any) {
    return new Promise(async (resolve, reject) => {
      try {
        const questions = visitsToSync.reduce((acc: any[], item: any) => {
          const visitId = item.id;
          const siteId = item.site?._id || '';
          item.questions.map((q: any) => {
            acc.push({
              id: q.id,
              visit: {id: visitId},
              siteId: siteId,
              question_category_id: q.relatedCategory,
              questionOrder: q.questionOrder,
              template_id: q._id || q.qb_id,
              answer: q.answer,
              naStatus: q?.naStatus || ''
            });
          });
          return acc;
        }, []);
        resolve(questions);
      } catch (e) {
        alert(`Can't read questions:` + JSON.stringify(e));
        reject({message: e})
      }
    });
  }

  readSyncQuestionsComments(visitsToSync: any) {
    return new Promise(async (resolve, reject) => {
      try {
        const questionsComments = visitsToSync.reduce((acc: any[], visit: any) => {
          const visitId = visit.id;
          if (visit.questions?.length) {
            visit.questions.forEach((question: any) => {
              const questionId = question.id;
              if (question.comments?.length) {
                question.comments.forEach((comment: any) => {
                  acc.push({
                    id: comment.id,
                    visitId: visitId,
                    questionId: questionId,
                    template_id: comment._id || comment.qb_id,
                    isCompleted: comment.isCompleted ? comment.isCompleted : false
                  });
                });
              }
            });
          }
          return acc;
        }, []);
        resolve(questionsComments);
      } catch (e) {
        alert(`Can't read questions comments:` + JSON.stringify(e));
        reject({message: e})
      }
    });
  }

  readSyncQuestionsTemplates(visits: any, date: any) {
    return new Promise(async (resolve, reject) => {
      const itemsFromDB: any = await this.db.readItemsFrom('questionTemplates', '_updatedDate', date) || [];
      let questionsTemplates = this.filterDataFromDB(itemsFromDB);
      let visitsIds = visits.map((item: any) => {
        return item.site._id;
      });
      try {
        questionsTemplates = questionsTemplates.map((item: any) => {
            let permanentlySitesRIDsAdd = [];
            let permanentlySitesRIDsRemove = [];
            let permanentlySitesRIDs = item.permanentlySitesRIDs?.split(',') || [];
            for (let id of visitsIds) {
              if (permanentlySitesRIDs.includes(id)) {
                permanentlySitesRIDsAdd.push(id);
              } else {
                permanentlySitesRIDsRemove.push(id);
              }
            }
            return {
              _id: item._id,
              permanentlySitesRIDsAdd: permanentlySitesRIDsAdd.join(','),
              permanentlySitesRIDsRemove: permanentlySitesRIDsRemove.join(',')
            };
          }
        );

        resolve(questionsTemplates);
      } catch (e) {
        alert(`Can't read Questions Templates: ` + JSON.stringify(e));
        reject({message: e})
      }
    });
  }

  readSyncNotes(visitsToSync: any) {
    return new Promise(async (resolve, reject) => {
      let notes: any = [];
      try {
        for (let visit of visitsToSync) {
          let trainingsNotes: any = [];
          let servicesNotes: any = [];
          let educationModulesNotes: any = [];
          let picturesAndNotes: any = [];
          let flowNotes: any = [];
          let siteInventoryNotes: any = [];
          let siteTemplateNotes: any = [];

          /* Trainings */
          try {
            if (visit.followUpNotes?.trainings?.length) {
              visit.followUpNotes.trainings.map((item: any) => {
                item.isPreviousVisit = true;
                item.id = this.utils.getUUIDv4();

                if (item?.comments?.length) {
                  item.comments.map((comment: any) => {
                    comment.id = comment.portal_id;
                    comment.isTrue = true;
                    if (comment.notes?.length) {
                      comment.notes = comment.notes.filter((note: any) => {
                        return note.isCompleted;
                      });
                    } else {
                      comment.notes = [];
                    }
                    return comment;
                  });
                }
                return item;
              });
              visit['trainingCategories'] = visit['trainingCategories'].concat(visit.followUpNotes.trainings);
            }
            for (let item of visit['trainingCategories']) {
              if (item.comments) {
                for (let comment of item.comments) {
                  if (comment.isTrue) {
                    if (!comment.notes?.length && comment.notesIds?.length) {
                      comment.notes = await this.db.readItemsByIds('notes', comment.notesIds) || [];
                    }
                    if (comment.notes) {
                      for (let n of comment.notes) {
                        n.relatedTrainingsCategory = comment.relatedTrainingsCategory;
                        n.trainingCommentId = comment.id;
                      }
                      trainingsNotes = trainingsNotes.concat(comment.notes);
                    }
                  }
                }
              }
            }
          } catch (e: any) {
            alert("Error during reading Training notes");
          }
          /* Services */
          try {
            if (visit.followUpNotes?.services?.length) {
              visit.followUpNotes.services.map((item: any) => {
                item.isPreviousVisit = true;
                item.id = this.utils.getUUIDv4();

                if (item.comments?.length) {
                  item.comments.map((comment: any) => {
                    comment.id = comment.portal_id;
                    comment.isTrue = true;
                    if (comment.notes?.length) {
                      comment.notes = comment.notes.filter((note: any) => {
                        return note.isCompleted;
                      });
                    } else {
                      comment.notes = [];
                    }
                    return comment;
                  });
                }

                return item;
              });
              visit['serviceCategories'] = visit['serviceCategories'].concat(visit.followUpNotes.services);
            }
            for (let item of visit['serviceCategories']) {
              if (item.comments) {
                for (let comment of item.comments) {
                  if (comment.isTrue) {
                    if (!comment.notes?.length && comment.notesIds?.length) {
                      comment.notes = await this.db.readItemsByIds('notes', comment.notesIds) || [];
                    }
                    if (comment.notes) {
                      for (let n of comment.notes) {
                        n.relatedServiceCategory = comment.relatedServiceCategory;
                        n.serviceCommentId = comment.id;
                      }
                      servicesNotes = servicesNotes.concat(comment.notes);
                    }
                  }
                }
              }
            }
          } catch (e: any) {
            alert("Error during reading Services notes");
          }
          /* Education Modules */
          try {
            if (visit.followUpNotes?.educationModules?.length) {
              visit.followUpNotes.educationModules.map((item: any) => {
                item.isPreviousVisit = true;
                item.id = this.utils.getUUIDv4();

                if (item?.comments?.length) {
                  item.comments.map((comment: any) => {
                    comment.id = comment.portal_id;
                    comment.isTrue = true;
                    if (comment.notes?.length) {
                      comment.notes = comment.notes.filter((note: any) => {
                        return note.isCompleted;
                      });
                    } else {
                      comment.notes = [];
                    }
                    return comment;
                  });
                }
                return item;
              });
              visit['educationModulesCategories'] = visit['educationModulesCategories'].concat(visit.followUpNotes.educationModules);
            }
            for (let item of visit['educationModulesCategories']) {
              const comments = [];
              if (item.comments) {
                for (let comment of item.comments) {
                  if (comment.isTrue) {
                    if (!comment.notes?.length && comment.notesIds?.length) {
                      comment.notes = await this.db.readItemsByIds('notes', comment.notesIds) || [];
                    }
                    if (comment.notes) {
                      for (let n of comment.notes) {
                        n.relatedServiceCategory = comment.relatedServiceCategory;
                        n.educationModuleCommentId = comment.id;
                      }
                      educationModulesNotes = educationModulesNotes.concat(comment.notes);
                    }
                  }
                }
              }
            }
          } catch (e: any) {
            alert("Error during reading Education modules notes");
          }
          /* Pictures and notes */
          try {
            picturesAndNotes = await this.db.readItemsByIds('notes', visit.picturesAndNotes) || [];
            if (visit.followUpNotes?.picturesAndNotes?.length) {
              visit.followUpNotes.picturesAndNotes.map((item: any) => {
                item.isPreviousVisit = true;
                return item;
              });
              visit.followUpNotes.picturesAndNotes = visit.followUpNotes.picturesAndNotes.filter((item: any) => {
                return item.isCompleted;
              });
              picturesAndNotes = picturesAndNotes.concat(visit.followUpNotes.picturesAndNotes);
            }
            picturesAndNotes = picturesAndNotes.filter((item: any) => {
              return item.noteSource === 'Pictures and Notes'
            });
          } catch (e: any) {
            alert("Error during reading Pictures and notes");
          }
          /* Flow */
          try {
            let flow: any = [];
            if (visit.followUpNotes?.flow?.length) {
              visit.followUpNotes.flow = visit.followUpNotes.flow.filter((flow: any) => {
                flow.additionalNotes = flow.additionalNotes.filter((additionalNote: any) => {
                  return additionalNote.isCompleted;
                });
                return flow.additionalNotes?.length || flow.note?.isCompleted;
              });
              flow = flow.concat(visit.followUpNotes.flow);
            }
            const questions = visit.questions?.length ? visit.questions.filter((item: any) => item.answer) : [];
            if (questions.length) {
              flow = flow.concat(questions);
            }

            for (let f of flow) {
              if (f.note?.id) {
                flowNotes = this.utils.uniqueConcat(flowNotes, f.note);
              } else if (f['noteID']) {
                f.note = await this.db.readItem('notes', f['noteID']);
                if (f.note?.id) {
                  f.note.questionId = f.id;
                  delete f.note.img;
                  flowNotes = this.utils.uniqueConcat(flowNotes, f.note);
                }
              }
              if (f.additionalNotes?.length) {
                flowNotes = this.utils.uniqueConcat(flowNotes, f.additionalNotes);
              } else if (f['additionalNotesIds'] && f['additionalNotesIds'].length) {
                f.additionalNotes = await this.db.readItemsByIds('notes', f['additionalNotesIds']) || [];
                for (let n of f.additionalNotes) {
                  n.questionId = f.id;
                  delete n.img;
                }
                flowNotes = this.utils.uniqueConcat(flowNotes, f.additionalNotes);
              }
            }
          } catch (e: any) {
            alert("Error during reading Flow notes");
          }
          /* Site inventory */
          try {
            let siteInventory: any = visit.siteInventory?.length ? visit.siteInventory.filter((item: any) => {
              return item.noteID;
            }) : [];
            for (let item of siteInventory) {
              if (!item.note?.id) {
                item.note = await this.db.readItem('notes', item['noteID']);
              }
              if (item.note?.id) {
                item.note.inventoryId = item.id;
              }
            }
            if (visit.followUpNotes?.siteInventory?.length) {
              visit.followUpNotes.siteInventory = visit.followUpNotes.siteInventory.map((item: any) => {
                item.isPreviousVisit = true;
                return item;
              });
              visit.followUpNotes.siteInventory = visit.followUpNotes.siteInventory.filter((item: any) => {
                return item?.note?.isCompleted;
              });
              siteInventory = siteInventory.concat(visit.followUpNotes.siteInventory);
            }
            siteInventoryNotes = siteInventory.map((item: any) => item.note);
            siteInventoryNotes = siteInventoryNotes.filter((item: any) => {
              return item.visitId;
            });
          } catch (e: any) {
            alert("Error during reading Service inventory notes");
          }
          const siteTemplate: any = await this.db.readItemsByField('sitesTemplates', 'relatedSite', visit['relatedSite']);

          if (siteTemplate?.followUpNotes) {
            siteTemplateNotes = [
              ...siteTemplate.followUpNotes.flow,
              ...siteTemplate.followUpNotes.trainings,
              ...siteTemplate.followUpNotes.services,
              ...siteTemplate.followUpNotes.educationModules,
              ...siteTemplate.followUpNotes.siteInventory,
              ...siteTemplate.followUpNotes.followUpNotes
            ];
          }

          notes = [...notes, ...flowNotes, ...picturesAndNotes, ...trainingsNotes, ...servicesNotes, ...educationModulesNotes, ...siteInventoryNotes, ...siteTemplateNotes];
        }
        notes = notes.map((item: any) => {
          if (item.portal_id) {
            item.id = item.portal_id;
          }
          if (item.relatedSite) {
            item.siteId = item.relatedSite;
          }
          if (!item.typeOfNote) {
            item.typeOfNote = item.noteType;
          }
          if (item.img) {
            delete item.img;
          }
          if (item._id) {
            delete item._id;
          }
          if (item._v) {
            delete item._v;
          }
          return item;
        });
        resolve(notes);
      } catch (e) {
        alert(`Can't read notes:` + JSON.stringify(e));
        reject({message: e})
      }
    });
  }

  readSyncTrainings(visitsToSync: any) {
    return new Promise(async (resolve, reject) => {
      try {
        const trainings = visitsToSync.reduce((acc: any[], item: any) => {
          const visitId = item.id;
          if (item.trainingCategories?.length) {
            item.trainingCategories.map((category: any) => {
              if (!category.isPreviousVisit) {
                const trainingCategoryId = category._id || category.qb_id || (category.comments?.length ? category.comments[0].relatedTrainingsCategory : '');
                const id = category.id || this.utils.getUUIDv4();
                if (id && trainingCategoryId && visitId) {
                  acc.push({
                    id: id,
                    visitId: visitId,
                    trainingCategoryId
                  });
                }
              }
            });
          }
          return acc;
        }, []);
        resolve(trainings);
      } catch (e) {
        alert(`Can't read questions trainings:` + JSON.stringify(e));
        reject({message: e})
      }
    });
  }

  readSyncTrainingsComments(visitsToSync: any) {
    return new Promise(async (resolve, reject) => {
      try {
        const trainingsComments = visitsToSync.reduce((acc: any[], visit: any) => {
          const visitId = visit.id;
          if (visit.trainingCategories?.length) {
            visit.trainingCategories.forEach((category: any) => {
              if (!category.isPreviousVisit) {
                const trainingCategoryId = category._id || category.qb_id;
                const trainingId = category.id;
                if (category.comments?.length) {
                  category.comments.forEach((comment: any) => {
                    acc.push({
                      id: comment.id,
                      visitId: visitId,
                      trainingCategoryId: trainingCategoryId,
                      trainingId: trainingId,
                      template_id: comment._id || comment.qb_id,
                      isTrue: comment?.isTrue ? comment.isTrue : false,
                    });
                  });
                }
              }
            });
          }
          return acc;
        }, []);
        resolve(trainingsComments);
      } catch (e) {
        alert(`Can't read questions training comments:` + JSON.stringify(e));
        reject({message: e})
      }
    });
  }

  readSyncServices(visitsToSync: any) {
    return new Promise(async (resolve, reject) => {
      try {
        const services = visitsToSync.reduce((acc: any[], item: any) => {
          const visitId = item.id;
          if (item.serviceCategories?.length) {
            item.serviceCategories.map((category: any) => {
              if (!category.isPreviousVisit) {
                const serviceCategoryId = category._id || category.qb_id || (category.comments?.length ? category.comments[0].relatedServiceCategory : '');
                const id = category.id || this.utils.getUUIDv4();
                if (id && serviceCategoryId && visitId) {
                  acc.push({
                    id: id,
                    visitId: visitId,
                    serviceCategoryId: serviceCategoryId,
                  });
                }
              }
            });
          }
          return acc;
        }, []);
        resolve(services);
      } catch (e) {
        alert(`Can't read questions services:` + JSON.stringify(e));
        reject({message: e})
      }
    });
  }

  readSyncServicesComments(visitsToSync: any) {
    return new Promise(async (resolve, reject) => {
      try {
        const servicesComments = visitsToSync.reduce((acc: any[], visit: any) => {
          const visitId = visit.id;
          if (visit.serviceCategories?.length) {
            visit.serviceCategories.forEach((category: any) => {
              if (!category.isPreviousVisit) {
                const serviceCategoryId = category._id || category.qb_id;
                const serviceId = category.id;
                if (category.comments?.length) {
                  category.comments.forEach((comment: any) => {
                    acc.push({
                      id: comment.id,
                      visitId: visitId,
                      serviceCategoryId: serviceCategoryId,
                      serviceId: serviceId,
                      template_id: comment._id || comment.qb_id,
                      isTrue: comment?.isTrue ? comment.isTrue : false
                    })
                  });
                }
              }
            });
          }
          return acc;
        }, []);
        resolve(servicesComments);
      } catch (e) {
        alert(`Can't read questions services comments:` + JSON.stringify(e));
        reject({message: e})
      }
    });
  }

  readSyncEducationModules(visitsToSync: any) {
    return new Promise(async (resolve, reject) => {
      try {
        const educationModules = visitsToSync.reduce((acc: any[], item: any) => {
          const visitId = item.id;
          if (item.educationModulesCategories?.length) {
            item.educationModulesCategories.map((category: any) => {
              if (!category.isPreviousVisit) {
                const educationModuleCategoryId = category._id || category.qb_id || (category.comments?.length ? category.comments[0].relatedEducationModuleCategory : '');
                const id = category.id || this.utils.getUUIDv4();
                if (id && educationModuleCategoryId && visitId) {
                  acc.push({
                    id: id,
                    visitId: visitId,
                    educationModuleCategoryId
                  });
                }
              }
            });
          }
          return acc;
        }, []);
        resolve(educationModules);
      } catch (e) {
        alert(`Can't read questions education modules:` + JSON.stringify(e));
        reject({message: e})
      }
    });
  }

  readSyncEducationModulesComments(visitsToSync: any) {
    return new Promise(async (resolve, reject) => {
      try {
        const educationModulesComments = visitsToSync.reduce((acc: any[], visit: any) => {
          const visitId = visit.id;
          if (visit.educationModulesCategories?.length) {
            visit.educationModulesCategories.forEach((category: any) => {
              if (!category.isPreviousVisit) {
                const educationModuleCategoryId = category._id || category.qb_id;
                const educationModuleId = category.id;
                if (category.comments?.length) {
                  category.comments.forEach((comment: any) => {
                    acc.push({
                      id: comment.id,
                      visitId: visitId,
                      educationModuleCategoryId: educationModuleCategoryId,
                      educationModuleId: educationModuleId,
                      template_id: comment._id || comment.qb_id,
                      isTrue: comment?.isTrue ? comment.isTrue : false
                    })
                  });
                }
              }
            });
          }
          return acc;
        }, []);
        resolve(educationModulesComments);
      } catch (e) {
        alert(`Can't read questions education modules comments:` + JSON.stringify(e));
        reject({message: e})
      }
    });
  }

  readSyncInventories(visitsToSync: any) {
    return new Promise(async (resolve, reject) => {
      try {
        const inventories = visitsToSync.reduce((acc: any[], item: any) => {
          const visitId = item.id;
          const siteId = item.site?._id;
          if (item.siteInventory?.length) {
            item.siteInventory.map((item: any) => {
              if (item.id) {
                acc.push({
                  id: item.id,
                  siteId: siteId,
                  inventoryId: item.relatedInventory,
                  parLevel: item.parLevel,
                  eachCase: item.eachOrCase || "Each",
                  visitId: visitId,
                  onHand: item.onHand || 0,
                  toDeliver: item.toDeliver || 0,
                });
              }
            });
          }
          return acc;
        }, []);
        resolve(inventories);
      } catch (e) {
        alert(`Can't read inventories:` + JSON.stringify(e));
        reject({message: e})
      }
    });
  }

  readSyncTruckInventories(date: any) {
    return new Promise(async (resolve, reject) => {
      const itemsFromDB: any = await this.db.readItemsFrom('truckInventoryTemplates', '_updatedDate', date) || [];
      const tuckInventoriesRecords = this.filterDataFromDB(itemsFromDB);
      try {
        const tuckInventories = tuckInventoriesRecords.map((item: any) => {
          item.id = item._id || item.qb_id;
          return item;
        });
        resolve(tuckInventories);
      } catch (e) {
        alert(`Can't read truck inventories:` + JSON.stringify(e));
        reject({message: e})
      }
    });
  }

  readSyncClockInOut(date: any) {
    return new Promise(async (resolve, reject) => {
      const itemsFromDB: any = await this.db.readItemsFrom('clockInOut', '_updatedDate', date) || [];
      let clockInOut = this.filterDataFromDB(itemsFromDB);
      try {
        clockInOut = clockInOut.map((item: any) => {
            return {
              id: item.id,
              startDate: this.utils.getPacificDateISO(item.startDate),
              endDate: this.utils.getPacificDateISO(item.endDate),
              _createdDate: this.utils.getPacificDateISO(item._createdDate),
              _updatedDate: this.utils.getPacificDateISO(item._updatedDate),
              type: item.type,
              skipDate: item.skipDate ? this.utils.getPacificDateISO(item.skipDate) : ''
            };
          }
        );

        resolve(clockInOut);
      } catch (e) {
        alert(`Can't read clock in/out:` + JSON.stringify(e));
        reject({message: e})
      }
    });
  }

  uploadFiles(data: any) {
    return new Promise(async (resolve, reject) => {
      const notes: any = data.filter((item: any) => item.filename).map((item: any) => {
        return {
          noteId: item.id,
          filename: item.filename
        };
      });
      const files: any = await this.readFiles(notes);

      const obsArray = [];

      for (let f of files) {
        if (f.base64) {
          obsArray.push(this.rest.uploadFiles([f]));
        }
      }

      if (obsArray.length) {
        const obsZip = zip(...obsArray);
        obsZip.subscribe(
          async result => {
            let isError = false;
            for (let r of result as { message?: string }[]) {
              if (r?.message !== 'OK!') {
                isError = true;
              }
            }
            if (!isError) {
              resolve({message: 'All files were uploaded'});
            } else {
              resolve({message: 'Uploading files error'});
            }
          }, error => {
            resolve({message: 'Uploading files error'});
          });
      } else {
        resolve({message: 'No files to upload'});
      }
    });
  }

  readFiles(notes: any) {
    return new Promise(async (resolve, reject) => {
      const files = [];
      for (const note of notes) {
        if (note.filename) {
          const base64Full: any = await this.utils.readFile(note.filename);
          if (base64Full) {
            const base64 = base64Full.substring(base64Full.indexOf(',') + 1);
            files.push({noteId: note.noteId, base64});
          }
        }
      }

      resolve(files);
    });
  }

  readOldVisits() {
    return new Promise(async (resolve, reject) => {
      const monthAgo = new Date(this.utils.getPacificDate().getTime() - 2629746000);
      const itemsFromDB: any = await this.db.readItems('visits') || [];
      const visits = itemsFromDB.filter((item: any) => {
        return item.finishDate <= monthAgo;
      });
      resolve(visits);
    });
  }

  async saveSiteTemplateFiles(siteTemplate: any) {
    return new Promise(async (resolve, reject) => {
      if (siteTemplate.followUpNotes) {
        const files: any = [];
        siteTemplate.followUpNotes.trainings = siteTemplate.followUpNotes.trainings.map((t: any) => {
          t.comments.map((comment: any) => {
            comment.notes.map((note: any) => {
              if (note.photoLink) {
                note.filename = `${note.portal_id}.png`;
                files.push({filename: note.filename, url: note.photoLink});
              }
              return note;
            });
            return comment;
          });
          return t;
        });

        siteTemplate.followUpNotes.services = siteTemplate.followUpNotes.services.map((s: any) => {
          s.comments.map((comment: any) => {
            comment.notes.map((note: any) => {
              if (note.photoLink) {
                note.filename = `${note.portal_id}.png`;
                files.push({filename: note.filename, url: note.photoLink});
              }
              return note;
            });
            return comment;
          });
          return s;
        });

        siteTemplate.followUpNotes.educationModules = siteTemplate.followUpNotes.educationModules.map((em: any) => {
          em.comments.map((comment: any) => {
            comment.notes.map((note: any) => {
              if (note.photoLink) {
                note.filename = `${note.portal_id}.png`;
                files.push({filename: note.filename, url: note.photoLink});
              }
              return note;
            });
            return comment;
          });
          return em;
        });

        siteTemplate.followUpNotes.flow = siteTemplate.followUpNotes.flow.map((f: any) => {
          f.additionalNotes.map((note: any) => {
            if (note.photoLink) {
              note.filename = `${note.portal_id}.png`;
              files.push({filename: note.filename, url: note.photoLink});
            }
            return note;
          });
          return f;
        });

        siteTemplate.followUpNotes.picturesAndNotes = siteTemplate.followUpNotes.picturesAndNotes.map((item: any) => {
          if (item.photoLink) {
            item.filename = `${item.portal_id}.png`;
            files.push({filename: item.filename, url: item.photoLink});
          }

          return item;
        });

        for (let file of files) {
          if (this.utils.isNative()) {
            const isFileExist = await this.utils.isFileExist(file.filename);
            if (!isFileExist) {
              const fileBlob = await firstValueFrom(this.rest.readFileByUrl(file.url));
              if (fileBlob) {
                const base64: any = await this.utils.blobToB64(fileBlob);
                const base64Data = base64.substring(base64.indexOf(',') + 1);
                await this.utils.writeFile(file.filename, base64Data);
              } else {
                alert(`Can't read file ${file.url}, please try again`);
                this.global.set('isLoading', false);
              }
            }
          }
        }
        await this.db.updateItem('sitesTemplates', siteTemplate.id, siteTemplate);
        resolve({'message': `${files.length} files found`})
      } else {
        resolve({'message': `0 files found`})
      }
    });
  }

  async saveVisits(visits: any) {
    return new Promise(async (resolve, reject) => {
      const isSyncImages = Boolean(this.storage.getItem('isSyncImages'));

      if (visits?.length) {
        const files: any = [];
        for (let visit of visits) {
          if (isSyncImages) {
            visit.trainingCategories = visit.trainingCategories?.map((t: any) => {
              t.comments.map((comment: any) => {
                comment.notes.map((note: any) => {
                  if (note.photoLink) {
                    note.filename = `${note.portal_id}.png`;
                    files.push({filename: note.filename, url: note.photoLink});
                  }
                  return note;
                });
                return comment;
              });
              return t;
            }) || [];

            visit.serviceCategories = visit.serviceCategories?.map((s: any) => {
              s.comments.map((comment: any) => {
                comment.notes.map((note: any) => {
                  if (note.photoLink) {
                    note.filename = `${note.portal_id}.png`;
                    files.push({filename: note.filename, url: note.photoLink});
                  }
                  return note;
                });
                return comment;
              });
              return s;
            }) || [];

            visit.educationModulesCategories = visit.educationModulesCategories?.map((em: any) => {
              em.comments.map((comment: any) => {
                comment.notes.map((note: any) => {
                  if (note.photoLink) {
                    note.filename = `${note.portal_id}.png`;
                    files.push({filename: note.filename, url: note.photoLink});
                  }
                  return note;
                });
                return comment;
              });
              return em;
            }) || [];

            visit.questions = visit.questions?.map((f: any) => {
              f.additionalNotes.map((note: any) => {
                if (note.photoLink) {
                  note.filename = `${note.portal_id}.png`;
                  files.push({filename: note.filename, url: note.photoLink});
                }
                return note;
              });
              return f;
            }) || [];

            if (visit.notes?.length) {
              visit.picturesAndNotes = visit.notes.map((item: any) => {
                if (item.photoLink) {
                  item.filename = `${item.portal_id}.png`;
                  files.push({filename: item.filename, url: item.photoLink});
                }
                return item.id;
              });

              await this.saveItems('notes', visit.notes);
              delete visit.notes;
            } else {
              visit.picturesAndNotes = [];
            }

            for (let file of files) {
              if (this.utils.isNative()) {
                const isFileExist = await this.utils.isFileExist(file.filename);
                if (!isFileExist) {
                  const fileBlob = await firstValueFrom(this.rest.readFileByUrl(file.url));
                  if (fileBlob) {
                    const base64: any = await this.utils.blobToB64(fileBlob);
                    const base64Data = base64.substring(base64.indexOf(',') + 1);
                    await this.utils.writeFile(file.filename, base64Data);
                  } else {
                    alert(`Can't read file ${file.url}, please try again`);
                    this.global.set('isLoading', false);
                  }
                }
              }
            }
          } else {
            if (visit.notes?.length) {
              visit.picturesAndNotes = visit.notes.map((item: any) => {
                return item.id;
              });
              await this.saveItems('notes', visit.notes);
              delete visit.notes;
            } else {
              visit.picturesAndNotes = [];
            }
          }
        }
        await this.saveItems('visits', visits);
      }
      resolve(visits);
    });
  }

  saveItems(tableName: string, data: any) {
    return new Promise(async (resolve, reject) => {
      if (data?.length) {
        let obsArray = [];

        for (let item of data) {
          if (!item.id) {
            item.id = item._id || this.utils.getUUIDv4();
          }
          item.isSynchronized = true;
          const obs = this.db.createItem(tableName, item);
          obsArray.push(obs);
        }
        if (obsArray.length) {
          const obsZip = zip(...obsArray);
          obsZip.subscribe(
            result => {
              resolve(result);
            }
          );
        }
      }
    });
  }

  async getVisitsFiles(visit: any) {
    return new Promise(async (resolve, reject) => {
      let files = [];
      const siteTemplate: any = await this.db.readItem('sitesTemplates', visit.site._id);
      if (siteTemplate['healthReport'].filename) {
        files.push(siteTemplate['healthReport'].filename);
      }
      if (siteTemplate['haccp'].filename) {
        files.push(siteTemplate['haccp'].filename);
      }

      const noteFiles = this.utils.getAllObjectParameters(visit, 'filename');

      files = files.concat(noteFiles);
      resolve(files);
    });
  }

  filterDataFromDB(itemsFromDB: any) {
    return itemsFromDB.filter((item: any) => {
      return !item.isSynchronized;
    });
  }

  async deleteItemsBeforeSync(tableName: string) {
    const items: any = await this.db.readItems(tableName);

    for (let item of items) {
      await this.db.deleteItem(tableName, item.id);
    }
  }

  async deleteOldItems() {
    const today = new Date();
    const lastMonth = new Date(today.setMonth(today.getMonth() - 1));

    const tables: string[] = [
      'visits',
      'notes',
      'routes',
    ];
    for (let tableName of tables) {
      const items: any = await this.db.readItems(tableName);
      const oldItems = items.filter((item: any) => {
        return item._createdDate < lastMonth;
      });

      for (let item of oldItems) {
        const filesToDelete = this.utils.findFilesInObject(item) || [];
        for (let filename of filesToDelete) {
          await this.utils.deleteFile(filename);
        }

        await this.db.deleteItem(tableName, item.id);
      }
    }
  }
}
